import { AxiosInstance } from 'axios';
import { InventoryCategory, InventoryGroup } from '../inventory/Inventory.types';
import { generateNotification } from '../notification/notification.util';
import { NotificationType } from '../notification/notification.types';
import i18n from '../../../client/src/i18n/i18n';
import { mapReplacer } from '..';

/**
 * GET Endpoint to load all categories
 * @param axios instance of axios
 * @returns List of all inventoryCategories
 */
export const getAllCategories = async (axios: AxiosInstance): Promise<InventoryCategory[]> => {
  return axios
    .get('/inventory/category/all')
    .then((res) => res.data)
    .catch((exc) => {
      console.error('Error while loading categories', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.getAllCategories'),
      });
      return [];
    });
};

/**
 * GET Endpoint to load all categories for a given group
 * @param axios instance of axios
 * @param group the inventoryGroup to load the categories for
 * @returns List of all inventoryCategories
 */
export const getAllCategoriesForGroup = async (
  axios: AxiosInstance,
  group: InventoryGroup,
): Promise<InventoryCategory[]> => {
  return axios
    .get('/inventory/category/group', { params: { group } })
    .then((res) => res.data)
    .catch((exc) => {
      console.error('Error while loading categories', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.getAllCategories'),
      });
      return [];
    });
};

/**
 * API Method to load a inventoryCategory by id
 * @param axios instance of axios
 * @param id the id of the category to load
 * @returns found category or undefined on error
 */
export const getCategoryById = async (
  axios: AxiosInstance,
  id: string,
): Promise<InventoryCategory> => {
  return axios
    .get('/inventory/category/', { params: { id } })
    .then((res) => res.data)
    .catch((exc) => {
      console.error('Error while loading categories', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.getCategoryById'),
      });
      return undefined;
    });
};

/**
 * API Method to create a new inventoryCategory
 * @param axios instance of axios
 * @param category the client side generated category
 * @returns the server side updated category or undefined on error
 */
export const createNewCategory = async (
  axios: AxiosInstance,
  category: InventoryCategory,
): Promise<InventoryCategory | undefined> => {
  return axios
    .post('/inventory/category/', JSON.stringify(category, mapReplacer))
    .then((res) => res.data)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.saveCategory'),
      });
      console.error('Error while updating category', exc);
      return undefined;
    });
};

/**
 * API Method to update an inventoryCategory
 * @param axios instance of axios
 * @param category the client side updated category
 * @returns the server side updated category or undefined on error
 */
export const updateCategory = async (
  axios: AxiosInstance,
  category: InventoryCategory,
): Promise<InventoryCategory | undefined> => {
  return axios
    .post('/inventory/category/update', JSON.stringify(category, mapReplacer))
    .then((res) => res.data)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.saveCategory'),
      });
      console.error('Error while updating category', exc);
      return undefined;
    });
};
